import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Header from '../components/Header';
import TopNewsHero from '../components/TopNewsHero';
import CategorySection from '../components/CategorySection';
import VideoSection from '../components/VideoSection';
import Footer from '../components/Footer';
import AdSlot from '../components/AdSlot';
import SEO, { absoluteUrl, SITE_NAME, DEFAULT_DESCRIPTION, DEFAULT_IMAGE } from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  {
    name: 'Violent Crimes',
    slug: 'violent',
    image: 'https://images.unsplash.com/photo-1718592168437-8382e5b97736?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwyfHxwb2xpY2UlMjBsaWdodHMlMjBuaWdodHxlbnwwfHx8fDE3ODA0MDkwOTN8MA&ixlib=rb-4.1.0&q=85'
  },
  {
    name: 'Cyber Crimes',
    slug: 'cyber',
    image: 'https://images.pexels.com/photos/5380669/pexels-photo-5380669.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940'
  },
  {
    name: 'Financial',
    slug: 'white-collar',
    image: 'https://images.pexels.com/photos/7876093/pexels-photo-7876093.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940'
  },
  {
    name: 'Family/Extramarital',
    slug: 'property',
    image: 'https://images.pexels.com/photos/12348489/pexels-photo-12348489.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940'
  },
  {
    name: 'Tragedy',
    slug: 'tragedy',
    image: 'https://images.unsplash.com/photo-1518709268805-4e9042af2176?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.0.3&q=85&w=600'
  },
  {
    name: 'Others',
    slug: 'others',
    image: 'https://images.pexels.com/photos/10466471/pexels-photo-10466471.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940'
  }
];

function HomePage() {
  const [featuredArticle, setFeaturedArticle] = useState(null);
  const [latestArticles, setLatestArticles] = useState([]);
  const [videos, setVideos] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchHeroContent();
    fetchVideos();
  }, []);

  const fetchHeroContent = async () => {
    try {
      const [featuredRes, latestRes] = await Promise.all([
        axios.get(`${API}/articles`, { params: { featured: true, limit: 1 } }),
        axios.get(`${API}/articles`, { params: { featured: false, limit: 5 } })
      ]);

      if (featuredRes.data.length > 0) {
        setFeaturedArticle(featuredRes.data[0]);
      }
      setLatestArticles(latestRes.data);
    } catch (error) {
      console.error('Error fetching hero content:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchVideos = async () => {
    try {
      const { data } = await axios.get(`${API}/videos`);
      setVideos(data);
    } catch (error) {
      console.error('Error fetching videos:', error);
    }
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFF8F0] flex items-center justify-center">
        <div className="text-2xl font-bold tracking-tight text-[#0A0A0A]" >
          Loading...
        </div>
      </div>
    );
  }

  const isSearching = searchQuery && searchQuery.trim().length > 0;

  // JSON-LD: Organization + WebSite (with sitelinks search) on the homepage
  const homeJsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'NewsMediaOrganization',
      name: SITE_NAME,
      url: absoluteUrl('/'),
      logo: absoluteUrl(DEFAULT_IMAGE),
      description: DEFAULT_DESCRIPTION,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: SITE_NAME,
      url: absoluteUrl('/'),
      potentialAction: {
        '@type': 'SearchAction',
        target: `${absoluteUrl('/')}?q={search_term_string}`,
        'query-input': 'required name=search_term_string',
      },
    },
  ];

  return (
    <div className="min-h-screen bg-[#FFF8F0]" data-testid="home-page">
      <SEO
        title="Crime News & Investigations"
        description={DEFAULT_DESCRIPTION}
        path="/"
        type="website"
        image={featuredArticle?.image_url || DEFAULT_IMAGE}
        jsonLd={homeJsonLd}
      />
      <Header onSearch={handleSearch} />

      <div className="max-w-[1600px] mx-auto px-6 pt-6">
        <AdSlot slot="top" height={90} />
      </div>

      {!isSearching && featuredArticle && (
        <TopNewsHero mainStory={featuredArticle} latestUpdates={latestArticles} />
      )}

      {isSearching && (
        <div className="max-w-[1600px] mx-auto px-6 pt-8" data-testid="search-results-header">
          <h2 className="text-3xl sm:text-4xl tracking-tight leading-none font-bold text-[#0A0A0A]" >
            SEARCH RESULTS FOR "{searchQuery.toUpperCase()}"
          </h2>
        </div>
      )}

      <div className="max-w-[1600px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 lg:gap-x-12 lg:divide-x lg:divide-[#E5E5E5]">
        <div className="lg:pr-0">
          <CategorySection
            category={CATEGORIES[0].name}
            categorySlug={CATEGORIES[0].slug}
            imageSrc={CATEGORIES[0].image}
            searchQuery={searchQuery}
          />
        </div>
        <div className="lg:pl-12">
          <CategorySection
            category={CATEGORIES[1].name}
            categorySlug={CATEGORIES[1].slug}
            imageSrc={CATEGORIES[1].image}
            searchQuery={searchQuery}
          />
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto px-6">
        <AdSlot slot="inline" height={250} />
      </div>

      <VideoSection videos={videos} />

      <div className="max-w-[1600px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 lg:gap-x-12 lg:divide-x lg:divide-[#E5E5E5]">
        <div>
          <CategorySection
            category={CATEGORIES[2].name}
            categorySlug={CATEGORIES[2].slug}
            imageSrc={CATEGORIES[2].image}
            searchQuery={searchQuery}
          />
        </div>
        <div className="lg:pl-12">
          <CategorySection
            category={CATEGORIES[3].name}
            categorySlug={CATEGORIES[3].slug}
            imageSrc={CATEGORIES[3].image}
            searchQuery={searchQuery}
          />
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 lg:gap-x-12 lg:divide-x lg:divide-[#E5E5E5]">
        <div>
          <CategorySection
            category={CATEGORIES[4].name}
            categorySlug={CATEGORIES[4].slug}
            imageSrc={CATEGORIES[4].image}
            searchQuery={searchQuery}
          />
        </div>
        <div className="lg:pl-12">
          <CategorySection
            category={CATEGORIES[5].name}
            categorySlug={CATEGORIES[5].slug}
            imageSrc={CATEGORIES[5].image}
            searchQuery={searchQuery}
          />
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto px-6 pb-6">
        <AdSlot slot="bottom" height={90} />
      </div>

      <Footer />
    </div>
  );
}

export default HomePage;
